import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  ArrowRight,
  Building2,
  Mail,
  Phone,
  Sparkles,
  Sun,
  Moon,
  Check,
  User,
} from "lucide-react";
import landingService, { LandingContent } from "../../../services/landingService";
import {
  DEFAULT_LANDING_CONTENT,
  LANDING_CACHE_KEY as CACHE_KEY,
  mergeLandingContent,
  resolveImage,
} from "../landingDefaults";
import { useTheme } from "../../../contexts/ThemeContext";
import "../LandingPage.css";
import "./ContactSalesPage.css";

// Reads the same cache LandingPageSimple writes on its own successful fetch,
// so a visitor who already saw the landing page gets this page's real content
// instantly too, instead of the built-in defaults while the request is in flight.
const readCachedContent = (): LandingContent | null => {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    return raw ? mergeLandingContent(JSON.parse(raw)) : null;
  } catch {
    return null;
  }
};

const ContactSalesPage: React.FC = () => {
  const navigate = useNavigate();
  const { theme, toggleTheme } = useTheme();
  const [content, setContent] = useState<LandingContent | null>(readCachedContent);

  useEffect(() => {
    let cancelled = false;
    landingService
      .getPublicContent()
      .then((stored) => {
        if (!cancelled) setContent(mergeLandingContent(stored));
      })
      .catch(() => {
        if (!cancelled) setContent((prev) => prev ?? DEFAULT_LANDING_CONTENT);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const data = (content ?? DEFAULT_LANDING_CONTENT).contactSales;
  const nav = (content ?? DEFAULT_LANDING_CONTENT).nav;
  const themeToggleLabel = theme === "light" ? "Switch to dark theme" : "Switch to light theme";

  const phoneDigits = data.phone.replace(/[^\d+]/g, "");
  const mailHref = `mailto:${data.email}`;
  const telHref = `tel:${phoneDigits}`;

  return (
    <div className="landing-page min-h-screen csp-page">
      <header className="site-nav is-scrolled">
        <div className="site-nav-inner">
          <button
            type="button"
            className="hero-brand"
            onClick={() => navigate("/")}
            aria-label={nav.logoAlt}
          >
            <img src={resolveImage(nav.logo)} alt={nav.logoAlt} className="hero-brand-image" />
          </button>

          <div className="site-nav-actions">
            <button
              type="button"
              className="lp-theme-toggle"
              onClick={toggleTheme}
              aria-label={themeToggleLabel}
              title={themeToggleLabel}
            >
              {theme === "light" ? <Moon /> : <Sun />}
            </button>
            <button type="button" className="hero-login-link" onClick={() => navigate("/")}>
              <ArrowLeft className="w-4 h-4" style={{ marginRight: 6 }} />
              Back to Home
            </button>
          </div>
        </div>
      </header>

      <main className="csp-main">
        <div className="container csp-layout">
          <section className="csp-intro" aria-labelledby="csp-title">
            <span className="csp-kicker csp-anim">
              <Sparkles className="w-3.5 h-3.5" />
              Channel Partner
            </span>
            <h1 id="csp-title" className="csp-title csp-anim">
              {data.subheading}
            </h1>
            <p className="csp-description csp-anim">{data.description}</p>

            {data.highlights.length > 0 && (
              <ul className="csp-highlights csp-anim">
                {data.highlights.map((item) => (
                  <li key={item}>
                    <span className="csp-check" aria-hidden="true">
                      <Check className="w-3.5 h-3.5" />
                    </span>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            )}
          </section>

          <aside className="csp-contact-card csp-anim" aria-label="Contact details">
            <div className="csp-contact-header">
              <div className="csp-contact-avatar">
                <Building2 className="w-6 h-6" />
              </div>
              <div>
                <h2>{data.companyName}</h2>
                <p>Reach your channel partner directly</p>
              </div>
            </div>

            <div className="csp-contact-list">
              <div className="csp-contact-item">
                <span className="csp-contact-icon">
                  <User className="w-4 h-4" />
                </span>
                <span className="csp-contact-text">
                  <span className="csp-contact-label">Contact person</span>
                  <span className="csp-contact-value">{data.contactName}</span>
                  {data.contactRole && <small>{data.contactRole}</small>}
                </span>
              </div>

              <a className="csp-contact-item csp-contact-link" href={telHref}>
                <span className="csp-contact-icon">
                  <Phone className="w-4 h-4" />
                </span>
                <span className="csp-contact-text">
                  <span className="csp-contact-label">Phone</span>
                  <span className="csp-contact-value">{data.phone}</span>
                </span>
                <ArrowRight className="csp-contact-arrow w-4 h-4" aria-hidden="true" />
              </a>

              <a className="csp-contact-item csp-contact-link" href={mailHref}>
                <span className="csp-contact-icon">
                  <Mail className="w-4 h-4" />
                </span>
                <span className="csp-contact-text">
                  <span className="csp-contact-label">Email</span>
                  <span className="csp-contact-value">{data.email}</span>
                </span>
                <ArrowRight className="csp-contact-arrow w-4 h-4" aria-hidden="true" />
              </a>
            </div>

            <div className="csp-contact-actions">
              <a href={mailHref} className="csp-btn csp-btn-primary">
                <Mail className="w-4 h-4" />
                Email Us
              </a>
              <a href={telHref} className="csp-btn csp-btn-secondary">
                <Phone className="w-4 h-4" />
                Call Us
              </a>
            </div>
          </aside>
        </div>
      </main>

      <footer className="csp-footer">
        <div className="container">
          &copy; {new Date().getFullYear()} {data.companyName}. All rights reserved.
        </div>
      </footer>
    </div>
  );
};

export default ContactSalesPage;
